import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { usePreferences } from '../contexts/PreferencesContext';
import { API_BASE, authenticatedFetch } from '../utils';

/**
 * Follow-ups: the work queue behind GET /action-items.
 *
 * TWO DEFECTS THIS FILE EXISTS TO CLOSE
 * -------------------------------------
 * 1. **The window.** The old hook sent whatever `startDate`/`endDate` the screen
 *    held, and the screen defaulted to today — so an item raised yesterday and
 *    still not done simply stopped existing at midnight. A backlog is not a
 *    period. Dates are sent ONLY when the caller passes both, and the screen
 *    leaves them out by default.
 *
 * 2. **Three surfaces disagreeing.** `action_status` is read by four different
 *    endpoints: this one, `/api/v2/dashboard` (follow-up closure and the ageing
 *    block), `/api/v2/quality` and `/api/v2/nav-counts` (the sidebar badge), and
 *    `/calls` (which selects `*`, so every row carries its follow-up state).
 *    Invalidating only `['actionItems']` left an item marked done here still
 *    showing as open on the Overview and in the Calls table until their caches
 *    aged out. Every mutation below invalidates all of them —
 *    `ACTION_STATUS_QUERY_KEYS` is the single list, so a new reader is added in
 *    one place rather than discovered as a bug.
 */

/** The only three values `/calls/{id}/action-status` accepts. */
export const ACTION_STATUSES = ['pending', 'in_progress', 'done'];

/** Per-agent count field for each status, as `/action-items` names them. */
const COUNT_FIELD = {
  pending: 'pending_count',
  in_progress: 'in_progress_count',
  done: 'done_count',
};

/**
 * Every query root whose payload is derived from `action_status`. Anything that
 * changes a follow-up's state must invalidate all of them or the console starts
 * telling the user two different things about the same item.
 *
 * `['analytics']` is deliberately the whole root rather than the three specific
 * endpoints under it: dashboard, quality, agents, callbacks and nav-counts all
 * read the same column set (see Backend/analytics/common.py), and a per-endpoint
 * list would go stale the first time another one starts counting follow-ups.
 */
export const ACTION_STATUS_QUERY_KEYS = [
  ['actionItems'],
  ['analytics'],
  ['calls'],
  ['call-count'],
];

/** Anything the server has not set yet is pending — the same default it applies. */
export const normaliseActionStatus = (raw) => {
  const s = String(raw || '').trim().toLowerCase();
  return ACTION_STATUSES.includes(s) ? s : 'pending';
};

/**
 * Throw with the server's own sentence where it gave us one, because the error
 * EmptyState renders `message` and "Call not found or access denied" is a far
 * more actionable thing to read than "Request failed".
 */
const request = async (path, options) => {
  const response = await authenticatedFetch(`${API_BASE}${path}`, options);
  if (!response.ok) {
    let detail = '';
    try {
      const body = await response.json();
      detail = body?.detail || body?.message || '';
    } catch {
      /* a non-JSON error body is not worth a second failure */
    }
    const err = new Error(detail || `Request failed (${response.status})`);
    err.status = response.status;
    throw err;
  }
  return response.json();
};

/* ─── Reading the queue ──────────────────────────────────────────────────── */

/**
 * The follow-up queue, grouped by agent exactly as the server groups it.
 *
 * @param {object} [filters]
 * @param {'all'|'pending'|'in_progress'|'done'} [filters.status='all']
 *   Forwarded to the endpoint, which applies it as an equality filter. The
 *   Follow-ups screen deliberately asks for `all` and narrows in the browser:
 *   its default view is "not finished yet", which is `pending` OR `in_progress`
 *   and cannot be expressed as one `.eq`, and it needs the raised-ever total to
 *   tell "nothing is open — you're clear" apart from "nothing has been raised".
 *   One payload also keeps the per-agent counts complete for the ranked
 *   who-is-buried list.
 * @param {string} [filters.startDate] YYYY-MM-DD. Sent only WITH `endDate`.
 * @param {string} [filters.endDate]   YYYY-MM-DD. A half-open range is treated as
 *   no range at all: bounding one end and not the other silently reintroduces
 *   the vanishing-backlog bug from a different direction.
 *
 * @returns {object} TanStack Query result over
 *   `{ agents: [{ agent_name, items, pending_count, in_progress_count,
 *   done_count }], summary: { total, pending, in_progress, done } }`.
 */
export const useActionItems = (filters = {}) => {
  const { timeZone } = usePreferences();
  const status = filters.status || 'all';
  const bounded = Boolean(filters.startDate && filters.endDate);
  // The params object IS the cache key, so a cached unbounded backlog can never
  // be served under a bounded window's label. The dates are business-local days,
  // the same window the sidebar badge counts.
  const params = {
    status,
    start_date: bounded ? filters.startDate : '',
    end_date: bounded ? filters.endDate : '',
    tz: timeZone || '',
  };

  return useQuery({
    queryKey: ['actionItems', params],
    queryFn: () => {
      const search = new URLSearchParams();
      if (params.start_date) search.append('start_date', params.start_date);
      if (params.end_date) search.append('end_date', params.end_date);
      if (params.status && params.status !== 'all') search.append('status', params.status);
      if (params.tz) search.append('tz', params.tz);
      const qs = search.toString();
      return request(`/action-items${qs ? `?${qs}` : ''}`);
    },
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
    retry: 1,
    // A shared work queue: two people clearing the same backlog need to see each
    // other's progress, and this is one indexed query rather than an aggregation.
    refetchOnWindowFocus: true,
  });
};

/* ─── Optimistic cache surgery ───────────────────────────────────────────── */

/**
 * Move a count by `by`, but only when we actually have one.
 *
 * An absent count stays absent: `(undefined ?? 0) - 1` would invent a figure the
 * server never sent, which is the fabrication rule from lib/format applied to
 * cached data rather than to a rendered tile.
 */
const bump = (target, field, by) => {
  const current = target[field];
  if (typeof current !== 'number' || !Number.isFinite(current)) return;
  target[field] = Math.max(0, current + by);
};

/**
 * Return a copy of an `/action-items` payload with `ids` moved to `next`,
 * keeping the per-agent counts and the summary in step.
 *
 * Only touched agents are rebuilt, so untouched groups keep their identity and
 * React skips re-rendering them.
 */
const withStatusApplied = (data, ids, next) => {
  if (!data || !Array.isArray(data.agents)) return data;

  const wanted = ids instanceof Set ? ids : new Set(ids);
  if (!wanted.size) return data;

  // Accumulated across every agent, then applied once to `summary`.
  const summaryMoves = { pending: 0, in_progress: 0, done: 0 };
  let anyChange = false;

  const agents = data.agents.map((agent) => {
    const items = Array.isArray(agent?.items) ? agent.items : [];
    let agentChanged = false;
    const counts = {};

    const nextItems = items.map((item) => {
      if (!item || !wanted.has(item.callId)) return item;
      const from = normaliseActionStatus(item.action_status);
      if (from === next) return item;
      agentChanged = true;
      counts[from] = (counts[from] || 0) - 1;
      counts[next] = (counts[next] || 0) + 1;
      summaryMoves[from] -= 1;
      summaryMoves[next] += 1;
      return { ...item, action_status: next };
    });

    if (!agentChanged) return agent;
    anyChange = true;
    const nextAgent = { ...agent, items: nextItems };
    Object.entries(counts).forEach(([status, by]) => {
      if (by !== 0) bump(nextAgent, COUNT_FIELD[status], by);
    });
    return nextAgent;
  });

  if (!anyChange) return data;

  const summary = { ...(data.summary || {}) };
  ACTION_STATUSES.forEach((status) => {
    if (summaryMoves[status] !== 0) bump(summary, status, summaryMoves[status]);
  });

  return { ...data, agents, summary };
};

/**
 * Apply the optimistic patch to every cached `/action-items` payload — there is
 * one per scope the user has visited — and hand back the snapshots so a failure
 * can put all of them back exactly as they were.
 */
const patchCache = (queryClient, ids, next) => {
  const snapshots = queryClient.getQueriesData({ queryKey: ['actionItems'] });
  snapshots.forEach(([key, data]) => {
    queryClient.setQueryData(key, withStatusApplied(data, ids, next));
  });
  return snapshots;
};

const restoreCache = (queryClient, snapshots) => {
  if (!Array.isArray(snapshots)) return;
  snapshots.forEach(([key, data]) => queryClient.setQueryData(key, data));
};

const invalidateActionStatusReaders = (queryClient) => {
  ACTION_STATUS_QUERY_KEYS.forEach((queryKey) => queryClient.invalidateQueries({ queryKey }));
};

const putStatus = (callId, actionStatus) =>
  request(`/calls/${encodeURIComponent(callId)}/action-status`, {
    method: 'PUT',
    body: JSON.stringify({ action_status: normaliseActionStatus(actionStatus) }),
  });

/* ─── Writing ────────────────────────────────────────────────────────────── */

/**
 * Change one follow-up's status.
 *
 * The row repaints before the request lands, because a work queue where every
 * click costs a round trip before it acknowledges anything gets clicked twice.
 * A failure restores the exact previous cache rather than guessing the old value
 * back, and either way the settled handler invalidates every reader so the
 * server stays the authority.
 *
 * `mutate({ callId, actionStatus })`.
 */
export const useUpdateActionStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ callId, actionStatus }) => putStatus(callId, actionStatus),
    onMutate: async ({ callId, actionStatus }) => {
      // An in-flight refetch would land after the patch and undo it.
      await queryClient.cancelQueries({ queryKey: ['actionItems'] });
      return { snapshots: patchCache(queryClient, [callId], normaliseActionStatus(actionStatus)) };
    },
    onError: (_error, _variables, context) => restoreCache(queryClient, context?.snapshots),
    onSettled: () => invalidateActionStatusReaders(queryClient),
  });
};

/** Four at a time: enough to clear a selection quickly, few enough not to look like a flood. */
const BULK_CONCURRENCY = 4;

/**
 * Change many follow-ups at once.
 *
 * Resolves with `{ actionStatus, updated, failed }` instead of failing on the
 * first bad id, because "6 of 8 saved" is the truth in that case and the caller
 * has to be able to say so. It rejects only when NOTHING was written, which is
 * the one case where rolling the optimistic patch back is correct — a partial
 * write is reconciled by the invalidation, not by guessing.
 *
 * `mutate({ callIds, actionStatus })`.
 */
export const useBulkUpdateActionStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ callIds, actionStatus }) => {
      const status = normaliseActionStatus(actionStatus);
      const ids = [...new Set((Array.isArray(callIds) ? callIds : []).filter(Boolean))];
      const updated = [];
      const failed = [];

      for (let i = 0; i < ids.length; i += BULK_CONCURRENCY) {
        const slice = ids.slice(i, i + BULK_CONCURRENCY);
        // eslint-disable-next-line no-await-in-loop -- the batching IS the point
        const results = await Promise.allSettled(slice.map((id) => putStatus(id, status)));
        results.forEach((result, j) => {
          if (result.status === 'fulfilled') updated.push(slice[j]);
          else failed.push({ callId: slice[j], message: result.reason?.message || 'Update failed' });
        });
      }

      if (!updated.length && failed.length) {
        const err = new Error(failed[0].message);
        err.failed = failed;
        throw err;
      }
      return { actionStatus: status, updated, failed };
    },
    onMutate: async ({ callIds, actionStatus }) => {
      await queryClient.cancelQueries({ queryKey: ['actionItems'] });
      return {
        snapshots: patchCache(queryClient, callIds || [], normaliseActionStatus(actionStatus)),
      };
    },
    onError: (_error, _variables, context) => restoreCache(queryClient, context?.snapshots),
    onSettled: () => invalidateActionStatusReaders(queryClient),
  });
};
